import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft,
    FaCompass,
    FaBriefcase,
    FaGraduationCap,
    FaBook,
    FaInfoCircle,
    FaSearch,
    FaListOl,
} from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { formatAiText } from "../../utils/formatAiText";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const SECTIONS = [
    { key: "careerPaths", title: "Career Paths", icon: FaBriefcase, accent: "text-[#2148C0]", bg: "bg-blue-50" },
    { key: "universityPrograms", title: "University Programs", icon: FaGraduationCap, accent: "text-purple-600", bg: "bg-purple-50" },
    { key: "subjectCombinations", title: "Subject Combinations", icon: FaBook, accent: "text-emerald-600", bg: "bg-emerald-50" },
    { key: "admissionNotes", title: "Admission Notes", icon: FaInfoCircle, accent: "text-amber-600", bg: "bg-amber-50" },
];

const Section = ({ title, items, Icon, accent, bg }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
                <Icon className={accent} size={14} />
            </span>
            <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        </div>
        <ul className="space-y-2">
            {(items || []).map((item, i) => (
                <li key={i} className="text-sm text-gray-600 flex gap-2 leading-relaxed">
                    <span className={`${accent} mt-0.5`}>•</span> <span className="whitespace-pre-wrap">{formatAiText(item)}</span>
                </li>
            ))}
        </ul>
    </div>
);

const Guidance = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [goalInput, setGoalInput] = useState("");
    const [activeGoal, setActiveGoal] = useState("");
    // Guards against the mount-time generic fetch resolving after a
    // user-submitted goal fetch (or vice versa) and clobbering the newer
    // result — only the response matching the latest request is applied.
    const requestIdRef = useRef(0);

    const fetchGuidance = async (goal) => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const url = new URL(`${API_BASE_URL}/api/guidance`);
            if (goal) url.searchParams.set("goal", goal);
            const res = await fetch(url, { headers: authHeaders() });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || "Failed to load guidance");
            if (requestId !== requestIdRef.current) return; // a newer request has since started
            setData(json.data);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            toast.error(err.message);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchGuidance("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmitGoal = (e) => {
        e.preventDefault();
        const goal = goalInput.trim();
        setActiveGoal(goal);
        fetchGuidance(goal);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate("/dashboard")} className="text-white">
                    <FaArrowLeft size={18} />
                </button>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    <FaCompass /> Career & University Guidance
                </h1>
            </nav>

            <div className="max-w-4xl mx-auto p-6 space-y-5">
                <form
                    onSubmit={handleSubmitGoal}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col sm:flex-row gap-3"
                >
                    <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 mb-1 block">What do you want to study?</label>
                        <input
                            value={goalInput}
                            onChange={(e) => setGoalInput(e.target.value)}
                            placeholder="e.g. Medicine, Law, Computer Science..."
                            className="w-full border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30"
                        />
                    </div>
                    <button
                        disabled={loading}
                        className="self-end sm:self-auto flex items-center justify-center gap-2 bg-[#2148C0] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
                    >
                        <FaSearch size={12} /> Get guidance
                    </button>
                </form>

                {loading && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400 text-sm">
                        Building your{activeGoal ? ` ${activeGoal}` : ""} guidance...
                    </div>
                )}

                {!loading && data?.fitNote && (
                    <div className="bg-[#2148C0] rounded-2xl shadow-sm p-5 text-white">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{formatAiText(data.fitNote)}</p>
                    </div>
                )}

                {!loading && data?.recommendedJambSubjects?.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50">
                                <FaListOl className="text-[#2148C0]" size={14} />
                            </span>
                            <h3 className="font-semibold text-gray-800 text-sm">JAMB Subject Combination</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {data.recommendedJambSubjects.map((subject) => (
                                <span
                                    key={subject}
                                    className="text-sm font-medium text-[#2148C0] bg-blue-50 border border-blue-100 rounded-full px-3.5 py-1.5"
                                >
                                    {subject}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && data && (
                    <div className="grid md:grid-cols-2 gap-4">
                        {SECTIONS.map(({ key, title, icon, accent, bg }) => (
                            <Section key={key} title={title} items={data[key]} Icon={icon} accent={accent} bg={bg} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Guidance;
